import clsx from "clsx";
import { isMyBirthdayToday } from "@/lib/birthday";
import type { HeroEmployeeBirthday } from "@/lib/home";
import type { HeroProfile } from "@/lib/auth";

export function BirthdayWidget({
  me,
  todays,
  className,
  subtle = false,
}: {
  me: HeroProfile | null;
  todays: HeroEmployeeBirthday[];
  className?: string;
  /** Dezent-Modus: nur kompaktes Badge auf der Home-Seite. */
  subtle?: boolean;
}) {
  const mineIsToday = isMyBirthdayToday(me);
  const myUserId = me?.hero_user_id;
  const others = todays.filter((p) => !myUserId || p.user_id !== myUserId);

  if (subtle) {
    if (!mineIsToday) return null;
    const rawFirst = (me?.full_name || "").split(" ")[0]?.trim() || "dir";
    return (
      <section className={clsx("eh-home-birthday-badge-only", className)} aria-live="polite">
        <div className="eh-home-birthday-badge-only__inner">
          <span className="eh-home-birthday-badge-only__emoji" aria-hidden="true">
            🎂
          </span>
          <div className="min-w-0">
            <p className="eh-home-birthday-badge-only__title">
              Alles Gute zum Geburtstag, {rawFirst}!
            </p>
            <p className="eh-home-birthday-badge-only__meta">Schönen Tag!</p>
          </div>
        </div>
      </section>
    );
  }

  if (!mineIsToday && others.length === 0) return null;

  const rawFirst = (me?.full_name || "").split(" ")[0]?.trim() || "dir";

  return (
    <section className={clsx("eh-home-birthday-hero", className)} aria-live="polite">
      <div
        className={clsx(
          "eh-bday-card eh-bday-card--hero",
          mineIsToday ? "eh-bday-card--has-self" : "eh-bday-card--team",
        )}
      >
        {mineIsToday ? (
          <div className="eh-bday-hero-shell eh-bday-hero-shell--self">
            <div className="eh-bday-hero-inner">
              <div className="eh-bday-hero-visual" aria-hidden="true">
                <span className="eh-bday-hero-balloon">🎈</span>
                <span className="eh-bday-hero-cake">🎂</span>
                <span className="eh-bday-hero-balloon eh-bday-hero-balloon--r">🎈</span>
              </div>
              <p className="eh-bday-hero-kicker">Heute ist dein großer Tag</p>
              <h2 className="eh-bday-hero-title">
                Herzlichen Glückwunsch, {rawFirst}!
                <span className="eh-bday-hero-spark" aria-hidden="true">
                  ✨
                </span>
              </h2>
              <p className="eh-bday-hero-lead">
                Das Ernsthaus-Team wünscht dir einen schönen Geburtstag – Gesundheit, gute
                Momente und Zeit zum Auftanken.
              </p>
              <ul className="eh-bday-hero-wishes">
                <li>
                  <strong>Gönn dir eine Pause</strong> – der Tag gehört auch dir.
                </li>
                <li>
                  <strong>Lass dich feiern</strong> – privat oder mit Kolleg:innen.
                </li>
                <li>
                  <strong>Auf ein gelungenes neues Lebensjahr!</strong>
                </li>
              </ul>
              <div className="eh-bday-meta eh-bday-meta--hero">
                <div className="eh-bday-meta-item">
                  <span className="eh-bday-meta-label">Dein Tag</span>
                  <span className="eh-bday-meta-value">🎁 ☕ 🎵 ✨</span>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {others.length > 0 ? (
          <div
            className={clsx(
              "eh-bday-others",
              mineIsToday ? "eh-bday-others--with-self" : "eh-bday-others--solo",
            )}
          >
            {!mineIsToday ? (
              <div className="eh-bday-team-banner" aria-hidden="true">
                <span className="eh-bday-team-banner__emoji">🎉</span>
                <span className="eh-bday-team-banner__text">Geburtstag im Team</span>
              </div>
            ) : null}
            <strong>
              {mineIsToday ? "Außerdem heute Geburtstag" : "Heute feiern wir mit"}
            </strong>
            {mineIsToday ? (
              <p className="eh-bday-others-hint">
                Schick kurz eine Gratulation – das kommt immer gut an.
              </p>
            ) : null}
            <div className="eh-bday-chip-wrap">
              {others.map((p) => {
                const name = String(p.full_name || p.name || "").trim() || "—";
                return (
                  <span key={String(p.user_id ?? name)} className="eh-bday-chip" title={name}>
                    {name}
                  </span>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}
